/**
 * Atributos no painel (#214/#216): estado de um produto e seleção inicial do
 * editor. Lógica pura (testada em attributes.test.ts); a UI fica em
 * components/AttributesEditor.tsx e na tela Revisar atributos.
 *
 * Regra: só o atributo CONFIRMADO pelo dono é facto (dietas e picância são
 * segurança alimentar). Sem confirmação, o editor parte das sugestões da IA
 * (pré-marcadas) para o dono conferir e confirmar com um clique.
 */
import { dietTagName, type DietTag } from '@self-restaurant/shared'
import type { Product, ProductAttributes, ProductAttributesBody } from './api.js'

/**
 * Nome da dieta no idioma ativo: dicionário do servidor (diet.<tag>, semeado
 * na migração 0105) com fallback embutido do shared — nunca chave crua.
 */
export function dietTagLabel(
  t: (key: string) => string,
  tag: string,
  lang: string | undefined,
): string {
  const viaDict = t(`diet.${tag}`)
  if (viaDict === `diet.${tag}`) return dietTagName(tag as DietTag, lang)
  return viaDict
}

/** Dietas da lista fechada (shared DIET_TAGS) — a UI não inventa tag. */
export const DIET_TAG_OPTIONS = [
  'vegetarian',
  'vegan',
  'gluten_free',
  'lactose_free',
  'halal',
  'kosher',
] as const

export type AttributeState = 'confirmed' | 'pending' | 'unreviewed'

/**
 * confirmed = o dono confirmou (mesmo vazio: "sem atributos");
 * pending = há sugestão por confirmar; unreviewed = nunca revisado.
 */
export function attributeState(p: Pick<Product, 'attributes'>): AttributeState {
  if (p.attributes?.status === 'confirmed') return 'confirmed'
  return p.attributes?.status === 'suggested' ? 'pending' : 'unreviewed'
}

export type AttributeBadgeColor = 'gray' | 'yellow' | 'green'

/** Selo do catálogo: texto (chave i18n), cor e ícone por estado. */
export const ATTRIBUTE_BADGE: Record<
  AttributeState,
  { labelKey: string; color: AttributeBadgeColor; icon: string }
> = {
  unreviewed: { labelKey: 'admin.menu.attributes_state_none', color: 'gray', icon: '?' },
  pending: { labelKey: 'admin.menu.attributes_state_pending', color: 'yellow', icon: '!' },
  confirmed: { labelKey: 'admin.menu.attributes_state_confirmed', color: 'green', icon: '✓' },
}

/** Seleção inicial do editor: atributos existentes (sugeridos ou confirmados). */
export function initialAttributes(p: Pick<Product, 'attributes'>): Required<ProductAttributesBody> {
  const a = p.attributes
  return {
    diet_tags: a ? [...a.diet_tags] : [],
    spice_level: a?.spice_level ?? 0,
    flavor_tags: a ? [...a.flavor_tags] : [],
    is_kids: a?.is_kids ?? false,
    is_signature: a?.is_signature ?? false,
  }
}

/** Verdadeiro quando a seleção diverge do que está gravado. */
export function attributesChanged(
  p: Pick<Product, 'attributes'>,
  sel: Required<ProductAttributesBody>,
): boolean {
  const a = p.attributes
  if (!a) {
    return (
      sel.diet_tags.length > 0 ||
      sel.spice_level > 0 ||
      sel.flavor_tags.length > 0 ||
      sel.is_kids ||
      sel.is_signature
    )
  }
  return (
    a.diet_tags.length !== sel.diet_tags.length ||
    a.diet_tags.some((t) => !sel.diet_tags.includes(t)) ||
    a.spice_level !== sel.spice_level ||
    a.flavor_tags.length !== sel.flavor_tags.length ||
    a.flavor_tags.some((t) => !sel.flavor_tags.includes(t)) ||
    a.is_kids !== sel.is_kids ||
    a.is_signature !== sel.is_signature
  )
}

/** Liga/desliga uma dieta (ou tag de sabor) numa lista. */
export function toggleTag(list: string[], tag: string): string[] {
  return list.includes(tag) ? list.filter((t) => t !== tag) : [...list, tag]
}

/**
 * Produtos da fila «Revisar atributos» (#216): quem tem SUGESTÃO pendente —
 * atributos 'suggested' OU alergénios 'suggested' sem confirmação. Produtos
 * nunca revisados (sem sugestão nenhuma) ficam de fora: sem nada a conferir
 * não há fila (o editor do produto continua disponível para declarar).
 */
export function pendingAttributesReview(
  prods: Pick<Product, 'id' | 'attributes' | 'allergens' | 'allergens_confirmed_at'>[],
): string[] {
  return prods
    .filter((p) => attributeState(p) === 'pending' || allergensNeedReview(p))
    .map((p) => p.id)
}

/** Há sugestão de alergénio por confirmar? */
export function allergensNeedReview(
  p: Pick<Product, 'allergens' | 'allergens_confirmed_at'>,
): boolean {
  if (p.allergens_confirmed_at) return false
  return (p.allergens ?? []).some((a) => a.status === 'suggested')
}

/** Quantos atributos visíveis um produto confirmado tem (para o selo). */
export function hasAnyAttribute(a: ProductAttributes | null | undefined): boolean {
  if (!a) return false
  return (
    a.diet_tags.length > 0 ||
    a.spice_level > 0 ||
    a.flavor_tags.length > 0 ||
    a.is_kids ||
    a.is_signature
  )
}
